import { useState, useEffect } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { toast } from "react-toastify";

export const useRealtimeUserEvents = (uid, year) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!uid || !year) {
      setEvents([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const eventsRef = collection(db, "userEvents");
      const q = query(
        eventsRef,
        where("uid", "==", uid),
        where("year", "==", year.toString()),
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const eventsData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          // Sort by datetime on client side to avoid index requirement
          const sortedEvents = eventsData
            .filter((event) => event.status !== "rejected")
            .sort((a, b) => {
              const dateA = a.datetime ? new Date(a.datetime) : new Date(0);
              const dateB = b.datetime ? new Date(b.datetime) : new Date(0);
              return dateA - dateB;
            });

          setEvents(sortedEvents);
          setLoading(false);
        },
        (err) => {
          console.error("Error fetching real-time user events:", err);
          setError(err.message);
          setLoading(false);
          toast.error("Πρόβλημα στη σύνδεση με τη βάση δεδομένων");
        },
      );

      return () => unsubscribe();
    } catch (err) {
      console.error("Error setting up real-time listener:", err);
      setError(err.message);
      setLoading(false);
    }
  }, [uid, year]);

  return { events, loading, error };
};

export const useRealtimeAllUserEvents = (year, selectedUserId = null) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!year) {
      setEvents([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const eventsRef = collection(db, "userEvents");
      let q;
      
      if (selectedUserId) {
        q = query(
          eventsRef, 
          where("year", "==", year.toString()),
          where("uid", "==", selectedUserId)
        );
      } else {
        q = query(eventsRef, where("year", "==", year.toString()));
      }

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const eventsData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          console.log(eventsData);
          // Sort by datetime on client side to avoid index requirement
          const sortedEvents = eventsData.sort((a, b) => {
            const dateA = a.datetime ? new Date(a.datetime) : new Date(0);
            const dateB = b.datetime ? new Date(b.datetime) : new Date(0);
            return dateA - dateB;
          });

          setEvents(sortedEvents);
          setLoading(false);
        },
        (err) => {
          console.error("Error fetching real-time all user events:", err);
          setError(err.message);
          setLoading(false);
          toast.error("Πρόβλημα στη σύνδεση με τη βάση δεδομένων");
        },
      );

      return () => unsubscribe();
    } catch (err) {
      console.error("Error setting up real-time listener:", err);
      setError(err.message);
      setLoading(false);
    }
  }, [year, selectedUserId]);

  return { events, loading, error };
};

export const useRealtimeSpeakers = (year) => {
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!year) {
      setSpeakers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const speakersRef = collection(db, "speakers");
      const q = query(speakersRef, where("year", "==", year.toString()));

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const speakersData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          setSpeakers(speakersData);
          setLoading(false);
        },
        (err) => {
          console.error("Error fetching real-time speakers:", err);
          setError(err.message);
          setLoading(false);
          toast.error("Πρόβλημα στη σύνδεση με τη βάση δεδομένων");
        },
      );

      return () => unsubscribe();
    } catch (err) {
      console.error("Error setting up real-time listener:", err);
      setError(err.message);
      setLoading(false);
    }
  }, [year]);

  return { speakers, loading, error };
};

export const useRealtimeUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    try {
      const usersRef = collection(db, "users");

      const unsubscribe = onSnapshot(
        usersRef,
        (snapshot) => {
          const usersData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          // Sort by name for consistent ordering
          const sortedUsers = usersData.sort((a, b) => {
            const nameA = (a.name || "").toLowerCase();
            const nameB = (b.name || "").toLowerCase();
            return nameA.localeCompare(nameB);
          });

          setUsers(sortedUsers);
          setLoading(false);
        },
        (err) => {
          console.error("Error fetching real-time users:", err);
          setError(err.message);
          setLoading(false);
          toast.error("Πρόβλημα στη σύνδεση με τη βάση δεδομένων χρηστών");
        },
      );

      return () => unsubscribe();
    } catch (err) {
      console.error("Error setting up real-time users listener:", err);
      setError(err.message);
      setLoading(false);
    }
  }, []);

  return { users, loading, error };
};
